/**
 * Chapter 8, goals and targets: the objectives (14) and the targets as rings
 * (49).
 */
import type { ComponentType } from "react";
import { AtSign, Bookmark, Clock, Eye, Heart, MessageCircle, MousePointerClick, Search, Send, Target, Users } from "lucide-react";
import type { BusinessObjectiveKind, MeasureId, StrategyGoal, StrategyNumber } from "@/types/strategyDocument.type";
import {
  Eyebrow, Foot, Glyph, Headline, Slide, V,
  dayLong, figure, joinAnd, monthYear, num, platformName, slideText,
  type TemplateContext, FigureOr
} from "./kit";

const OBJECTIVE_HEADLINES: Record<BusinessObjectiveKind, string> = {
  known_by_more: "Be known *by more people.*",
  chosen_more: "Be chosen *more often.*",
  launch: "Launch *and be remembered.*",
  new_market: "Arrive *in a new market.*",
  change_perception: "Be seen *differently.*",
  keep_people: "Keep the people *we have.*",
  signups: "Turn attention *into sign-ups.*",
};

/** A target as the slide prints it: "4.5%", "450k", "25". */
const target = (g: StrategyGoal) => figure(g.target.value, g.target.unit, { lower: true });
const start = (n: StrategyNumber | null | undefined) => num(n, { lower: true });

/* ── 14 Objectives ────────────────────────────────────────────────────── */

export function ObjectivesSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const brief = doc.brief;
  const business = brief?.business_objective;
  const social = brief?.social_objective;
  const goals = doc.goals?.list ?? [];
  const t = slideText(ctx);
  const recap = brief?.recap;
  const measured = goals.find((g) => g.measure_id === business?.measure_id);
  const clientApprover = brief?.approvers.find((a) => a.role === "client")?.name ?? recap?.person;
  return (
    <Slide ink>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback={business ? OBJECTIVE_HEADLINES[business.kind] : "What social is for."} size={84} lineHeight={94} className="sv-hl-blue" />
        <div className="sv-ambition">
          {business ? (
            <p className="sv-ambition-line">
              The business objective{recap?.date ? <>, from {recap.occasion ?? "the request"} of <V>{dayLong(recap.date)}</V></> : null}:{" "}
              {business.number?.value != null ? (
                <>
                  <V>{measured?.short ?? measured?.name?.toLowerCase() ?? "the number"}</V>
                  {start(business.starting_point) ? <> from <V>{start(business.starting_point)}</V></> : null} to <V>{start(business.number)}</V>
                  {business.date ? <> by <V>{monthYear(business.date)}</V></> : null}.
                </>
              ) : <V>{business.line}</V>}
              {business.status === "to_confirm" && clientApprover ? <> To confirm with <V>{clientApprover}</V>.</> : null}
            </p>
          ) : null}
          {social ? (
            <>
              <p className="sv-key sv-ambition-key">So social will</p>
              <V as="p" className="sv-ambition-social">{social.text}</V>
              {social.line ? <V as="p" className="sv-ambition-line">{social.line}</V> : null}
            </>
          ) : null}
        </div>
        <span className="sv-push" />
        {goals.length ? (
          <div className="sv-goal-strip">
            {goals.slice(0, 3).map((g) => {
              const watched = g.id === doc.goals?.watch_most;
              return (
                <div key={g.id} className="sv-goal-card" data-watched={watched || undefined}>
                  <p className="sv-key"><V>{g.name ?? g.short}</V>{watched ? " · the number we watch" : null}</p>
                  <V as="p" className="sv-goal-figure">{target(g)}</V>
                  <p className="sv-goal-when">by <V>{monthYear(g.target.date)}</V>{start(g.starting_point) ? <> · from <V>{start(g.starting_point)}</V> today</> : null}</p>
                </div>
              );
            })}
          </div>
        ) : null}
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

/* ── 49 Targets ───────────────────────────────────────────────────────── */

const MEASURE_ICONS: Partial<Record<MeasureId, ComponentType<{ size?: number; strokeWidth?: number }>>> = {
  engagement_rate: Heart, reactions_per_view: Heart, reactions_per_post: Heart,
  followers_at_strategy: Users, follower_growth: Users,
  saves_per_1000: Bookmark, shares_per_1000: Send,
  comments_per_1000: MessageCircle, median_comments_per_post: MessageCircle, questions_answered: MessageCircle,
  views_per_post: Eye, reach: Eye, reach_beyond_followers: Eye, non_follower_share: Eye,
  watch_time: Clock, watched_to_end: Clock, search_presence: Search, posts_about_brand: AtSign,
  link_clicks: MousePointerClick, profile_visits: MousePointerClick,
};

/** "12 October, public read, Instagram and TikTok together". */
function fromWhen(g: StrategyGoal): string {
  const s = g.starting_point;
  if (!s) return "";
  const parts = [s.as_of ? dayLong(s.as_of) : null];
  if (s.access === "connected") parts.push(`connected ${joinAnd(g.platforms.map(platformName))}`);
  else if (s.access === "client") parts.push("from the client");
  else if (s.access === "public") parts.push("public read");
  if (s.access !== "connected" && g.platforms.length > 1) parts.push(`${joinAnd(g.platforms.map(platformName))} together`);
  return parts.filter(Boolean).join(", ");
}

function Ring({ share, watched }: { share: number; watched: boolean }) {
  const r = 110 - 8.8;
  const c = 2 * Math.PI * r;
  return (
    <svg className="sv-ring-svg" viewBox="0 0 220 220" aria-hidden>
      <circle cx="110" cy="110" r={r} fill="none" stroke="#ececec" strokeWidth="17.6" />
      <circle cx="110" cy="110" r={r} fill="none" stroke={watched ? "var(--blue)" : "#121212"} strokeWidth="17.6" strokeDasharray={`${c * share} ${c}`} transform="rotate(-90 110 110)" />
    </svg>
  );
}

export function TargetsSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const goals = doc.goals!;
  const t = slideText(ctx);
  const checks = goals.checks;
  const ticks: Array<{ ok: boolean; label: React.ReactNode }> = [];
  if (checks?.same_unit != null) ticks.push({ ok: checks.same_unit, label: "Same unit as the starting point" });
  if (checks?.same_covers != null) ticks.push({ ok: checks.same_covers, label: "Same accounts covered" });
  if (checks?.reachable_with_volume != null) ticks.push({ ok: checks.reachable_with_volume, label: "Reachable with the planned volume" });
  if (checks?.approved_by) ticks.push({ ok: true, label: <>Approved by <V>{checks.approved_by}</V>{checks.approved_by_client_at ? <>, <V>{dayLong(checks.approved_by_client_at)}</V></> : null}</> });
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="Three numbers, *one year.*" />
        <div className="sv-targets">
          {goals.list.slice(0, 3).map((g) => {
            const watched = g.id === goals.watch_most;
            const Icon = MEASURE_ICONS[g.measure_id] ?? Target;
            const from = g.starting_point?.value;
            const share = from != null && g.target.value > 0 ? Math.max(0, Math.min(1, from / g.target.value)) : 0;
            return (
              <div key={g.id} className="sv-target">
                <p className="sv-target-key">
                  {g.platforms.map((p) => <Glyph key={p} platform={p} size={14} />)}
                  <span className="sv-key"><V>{g.name ?? g.short}</V>{watched ? " · the number we watch" : null}</span>
                </p>
                <div className="sv-ring" data-watched={watched || undefined}>
                  <Ring share={share} watched={watched} />
                  <div className="sv-ring-centre">
                    <Icon size={30} strokeWidth={1.6} />
                    <V as="p" className="sv-ring-figure">{target(g)}</V>
                    <p className="sv-ring-when">by <V>{monthYear(g.target.date)}</V></p>
                  </div>
                </div>
                <div className="sv-target-from">
                  <p className="sv-target-from-figure">from <FigureOr value={start(g.starting_point)} /></p>
                  {fromWhen(g) ? <V as="p" className="sv-target-from-when">{fromWhen(g)}</V> : g.starting_point?.source?.what_would_settle_it ? <V as="p" className="sv-target-from-when sv-open">{g.starting_point.source.what_would_settle_it}</V> : null}
                </div>
                {g.target_source?.line ? <V as="p" className="sv-target-why">{g.target_source.line}</V> : null}
                {g.formula_name ? <V as="p" className="sv-target-formula">{g.formula_name}</V> : null}
              </div>
            );
          })}
        </div>
        {ticks.length ? (
          <div className="sv-checks">
            <p className="sv-key">Checks</p>
            {ticks.map((c, i) => (
              <p key={i} className="sv-check" data-ok={c.ok || undefined}><b>{c.ok ? "✓" : "✗"}</b><span>{c.label}</span></p>
            ))}
          </div>
        ) : null}
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}
